import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

// ─── AUTH ─────────────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem("hc_token");
    if (!t) {
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hc_token"))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("hc_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);
  const register = useCallback(async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("hc_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);
  const logout = useCallback(() => {
    localStorage.removeItem("hc_token");
    setUser(null);
  }, []);

  // Memoized context value so downstream `useContext(AuthCtx)` consumers
  // only re-render when `user`/`ready` actually change.
  const value = useMemo(
    () => ({
      user,
      ready,
      login,
      register,
      logout,
      setUser,
      culturePassActive: isPassActive(user),
    }),
    [user, ready, login, register, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

function isPassActive(user) {
  if (!user?.culture_pass_expires_at) return false;
  try {
    return new Date(user.culture_pass_expires_at).getTime() > Date.now();
  } catch {
    return false;
  }
}

// ─── CART ─────────────────────────────────────────────────────────────────────
const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hc_cart") || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem("hc_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, size = "") => {
    setItems((prev) => {
      const key = `${product.id}-${size}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          title: product.title,
          price: product.price,
          image: product.image,
          quantity: qty,
          size,
          category: product.category,
        },
      ];
    });
  };
  const removeItem = (key) =>
    setItems((prev) => prev.filter((i) => i.key !== key));
  const setQty = (key, qty) =>
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, qty) } : i))
    );
  const clear = () => setItems([]);
  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const value = useMemo(
    () => ({ items, addItem, removeItem, setQty, clear, total, count }),
    [items, total, count],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

// ─── PLAYER (audio queue) ─────────────────────────────────────────────────────
const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

export function PlayerProvider({ children }) {
  const [queue, setQueue] = useState([]); // [{id,title,artist,cover,url,ctype}]
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const current = queue[index] || null;

  const playNow = (track) => {
    setQueue([track]);
    setIndex(0);
    setIsPlaying(true);
  };
  const playList = (tracks, startIdx = 0) => {
    setQueue(tracks);
    setIndex(startIdx);
    setIsPlaying(true);
  };
  const enqueue = (track) => setQueue((q) => [...q, track]);
  const next = () =>
    setIndex((i) => (queue.length ? (i + 1) % queue.length : 0));
  const prev = () =>
    setIndex((i) => (queue.length ? (i - 1 + queue.length) % queue.length : 0));
  const toggle = () => setIsPlaying((p) => !p);
  const stop = () => {
    setIsPlaying(false);
    setQueue([]);
    setIndex(0);
  };

  const value = useMemo(
    () => ({
      queue,
      index,
      current,
      isPlaying,
      setIsPlaying,
      playNow,
      playList,
      enqueue,
      next,
      prev,
      toggle,
      stop,
    }),
    [queue, index, current, isPlaying],
  );

  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}
